import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

export async function authedFetch(path: string, token: string | null, init?: RequestInit): Promise<unknown> {
  const headers = new Headers(init?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init?.body) headers.set('Content-Type', 'application/json');
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const text = await res.text();
    let body: unknown = text;
    try { body = JSON.parse(text); } catch { /* leave as text */ }
    const err = new Error(`${res.status}: ${text}`) as Error & { status?: number; body?: unknown };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

// ─── Zod schemas ────────────────────────────────────────────────────────────

export const TenantStatusSchema = z.enum(['active', 'suspended', 'deleted']);

export const TenantSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  status: TenantStatusSchema,
  note: z.string().nullable().optional(),
});
export type Tenant = z.infer<typeof TenantSchema>;

export const WarehouseSchema = z.object({
  id: z.number(),
  tenant_id: z.number(),
  code: z.string(),
  name: z.string(),
  timezone: z.string(),
  address: z.string().nullable().optional(),
  created_at: z.string().optional(),
  auto_split_item_count_threshold: z.number().nullable().optional(),
  auto_split_per_row_qty_threshold: z.number().nullable().optional(),
  auto_split_mode: z.enum(['soft', 'hard']).optional(),
  auto_split_target_qty_per_operator: z.number().optional(),
  // picking-indexes — colli massimi consigliati per missione (default 100).
  max_qty_per_mission: z.number().optional(),
  // instradamento SFTP — sottocartella di /tpicking/out; null = nessuna.
  sftp_inbound_folder: z.string().nullable().optional(),
  // SP-A T7b — stato del magazzino. `optional` per retro-compatibilità: le
  // risposte precedenti al backend SP-A non lo espongono.
  status: z.enum(['active', 'suspended', 'deleted']).optional(),
  // Mail giornaliera dei parziali — `optional` per retro-compatibilita'.
  daily_partials_mail_enabled: z.boolean().optional(),
  daily_partials_mail_time: z.string().optional(),
  daily_partials_mail_recipients: z.array(z.string()).optional(),
  daily_partials_mail_last_closed_on: z.string().nullable().optional(),
});
export type Warehouse = z.infer<typeof WarehouseSchema>;

export const TenantSummarySchema = z.object({
  tenant_id: z.number(),
  warehouses_count: z.number(),
  tenant_admins_count: z.number(),
  voice_supervisors_count: z.number(),
  operators_count: z.number(),
  last_audit_at: z.string().nullable(),
});
export type TenantSummary = z.infer<typeof TenantSummarySchema>;

export const TenantAdminMembershipSchema = z.object({
  id: z.number(),
  user_id: z.number(),
  email: z.string().nullable(),
  display_name: z.string().nullable(),
  status: z.enum(['active', 'disabled', 'deleted']),
  assigned_at: z.string(),
});
export type TenantAdminMembership = z.infer<typeof TenantAdminMembershipSchema>;

// ─── List shapes ─────────────────────────────────────────────────────────────

const TenantsListSchema = z.object({ data: z.array(TenantSchema), cursor: z.string().nullable() });
const WarehousesListSchema = z.object({ data: z.array(WarehouseSchema), total: z.number() });
const TenantAdminsListSchema = z.object({ data: z.array(TenantAdminMembershipSchema), total: z.number() });

// ─── Query hooks ─────────────────────────────────────────────────────────────

export interface ListTenantsParams { search?: string; status?: 'active' | 'suspended' | 'all'; cursor?: string; }

export function useListTenants(token: string | null, params: ListTenantsParams = {}) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.cursor) qs.set('cursor', params.cursor);
  const path = `/admin/platform/tenants${qs.toString() ? '?' + qs.toString() : ''}`;
  return useQuery({
    queryKey: ['platform', 'tenants', params],
    queryFn: async () => TenantsListSchema.parse(await authedFetch(path, token)),
    enabled: token !== null,
  });
}

/** @deprecated Use useListTenants(token, params) instead */
export function useListTenantsWithToken(token: string | null) {
  return useListTenants(token);
}

export function useTenant(id: number | null, token: string | null) {
  return useQuery({
    queryKey: ['platform', 'tenants', id],
    queryFn: async () => TenantSchema.parse(await authedFetch(`/admin/platform/tenants/${id}`, token)),
    enabled: id !== null && id > 0 && token !== null,
  });
}

export function useTenantSummary(id: number | null, token: string | null) {
  return useQuery({
    queryKey: ['platform', 'tenants', id, 'summary'],
    queryFn: async () => TenantSummarySchema.parse(await authedFetch(`/admin/platform/tenants/${id}/summary`, token)),
    enabled: id !== null && id > 0 && token !== null,
  });
}

export function useTenantWarehouses(tenantId: number | null, token: string | null) {
  return useQuery({
    queryKey: ['platform', 'tenants', tenantId, 'warehouses'],
    queryFn: async () => WarehousesListSchema.parse(await authedFetch(`/admin/platform/tenants/${tenantId}/warehouses`, token)),
    enabled: tenantId !== null && token !== null,
  });
}

export function useTenantAdmins(tenantId: number | null, token: string | null) {
  return useQuery({
    queryKey: ['platform', 'tenants', tenantId, 'admins'],
    queryFn: async () => TenantAdminsListSchema.parse(await authedFetch(`/admin/platform/tenants/${tenantId}/admins`, token)),
    enabled: tenantId !== null && tenantId > 0 && token !== null,
  });
}

// ─── Mutation hooks ───────────────────────────────────────────────────────────

export interface CreateTenantBody { code: string; name: string; }

export function useCreateTenant(token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, idempotencyKey }: { body: CreateTenantBody; idempotencyKey: string }) =>
      TenantSchema.parse(await authedFetch('/admin/platform/tenants', token, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': idempotencyKey },
      })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  });
}

export function useUpdateTenant(id: number, token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: { name?: string; note?: string | null; status?: 'active' | 'suspended' }) =>
      TenantSchema.parse(await authedFetch(`/admin/platform/tenants/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  });
}

export function useSuspendTenant(id: number, token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      authedFetch(`/admin/platform/tenants/${id}/suspend`, token, { method: 'POST' }) as Promise<{ id: number; status: string }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  });
}

export function useReactivateTenant(id: number, token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      authedFetch(`/admin/platform/tenants/${id}/reactivate`, token, { method: 'POST' }) as Promise<{ id: number; status: string }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  });
}

export interface CreateWarehouseBody { code: string; name: string; timezone: string; address?: string | null; }

export function useCreateWarehouse(tenantId: number, token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, idempotencyKey }: { body: CreateWarehouseBody; idempotencyKey: string }) =>
      WarehouseSchema.parse(await authedFetch(`/admin/platform/tenants/${tenantId}/warehouses`, token, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': idempotencyKey },
      })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants', tenantId, 'warehouses'] }),
  });
}

export function useUpdateWarehouse(token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: Partial<Pick<Warehouse, 'name' | 'timezone' | 'address'>> }) =>
      WarehouseSchema.parse(await authedFetch(`/admin/platform/warehouses/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
  });
}

export interface InviteTenantAdminBody {
  email: string; display_name: string; password_temp: string; accept_existing?: boolean;
}

export function useInviteTenantAdmin(tenantId: number, token: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, idempotencyKey }: { body: InviteTenantAdminBody; idempotencyKey: string }) =>
      authedFetch(`/admin/platform/tenants/${tenantId}/admins`, token, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': idempotencyKey },
      }) as Promise<{ user_id: number; membership_id: number; kc_sub: string; did_create_kc: boolean }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenants', tenantId, 'admins'] }),
  });
}

export function useResetTenantAdminPassword(tenantId: number, token: string | null) {
  return useMutation({
    mutationFn: async (vars: { userId: number; temporary_password: string }) =>
      authedFetch(`/admin/platform/tenants/${tenantId}/admins/${vars.userId}/reset-password`, token, {
        method: 'POST',
        body: JSON.stringify({ temporary_password: vars.temporary_password }),
      }),
    // No invalidation — pure side-effect on KC, DB membership unchanged.
  });
}
